import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Card, ErrorState, Loading, Screen, Title } from './components/ui';
import { SessionProvider, useSession } from './lib/session';
import Admin from './screens/Admin';
import Book from './screens/Book';
import Cart from './screens/Cart';
import CustomerRecord from './screens/CustomerRecord';
import Customers from './screens/Customers';
import Home from './screens/Home';
import Login from './screens/Login';
import Bulawa from './screens/Bulawa';
import CallGuest from './screens/CallGuest';
import Join from './screens/Join';
import Nyauta from './screens/Nyauta';

/**
 * Routes (§7). The client guards mirror the server guards for a good experience —
 * but the SERVER is the authority (§0.6). Every screen's data comes from a
 * guarded endpoint, so a hand-typed URL shows nothing it should not.
 */
export default function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          {/*
            GUEST ROUTE. No session, no redirect to /login, ever — a guest has no
            account and never will (§3). The token alone opens it.
          */}
          <Route path="/n/:token" element={<Nyauta />} />
          <Route path="/b/:token" element={<Bulawa />} />
          <Route path="/join/:token" element={<Join />} />
          <Route path="/call/:token" element={<CallGuest />} />

          {/* The cart runs its own three-door flow and never redirects to /login. */}
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<RequireAnon />} />
          <Route path="/" element={<RequireStaff />} />
          <Route path="/customers" element={<StaffOnly><Customers /></StaffOnly>} />
          <Route path="/customers/:id" element={<StaffOnly><CustomerRecord /></StaffOnly>} />
          <Route path="/book" element={<StaffOnly><Book /></StaffOnly>} />
          <Route path="/admin" element={<RequireAdmin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}

function Gate({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/**
 * Client-side gate for any signed-in screen. The server guards the data
 * regardless (§0.6) — this only decides what is worth rendering.
 */
function StaffOnly({ children }: { children: React.ReactNode }) {
  const { state } = useSession();
  if (state.status === 'loading') return <Booting />;
  if (state.status === 'error') return <Broken message={state.message} />;
  if (state.status === 'anon') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAnon() {
  const { state } = useSession();
  if (state.status === 'loading') return <Booting />;
  if (state.status === 'error') return <Broken message={state.message} />;
  if (state.status === 'in') return <Navigate to="/" replace />;
  return (
    <Gate>
      <Login />
    </Gate>
  );
}

function RequireStaff() {
  const { state } = useSession();
  if (state.status === 'loading') return <Booting />;
  if (state.status === 'error') return <Broken message={state.message} />;
  if (state.status === 'anon') return <Navigate to="/login" replace />;
  // A member signed in AT a cart belongs on the cart screen.
  if (state.me.device === 'cart') return <Navigate to="/cart" replace />;
  return <Home me={state.me} />;
}

function RequireAdmin() {
  const { state } = useSession();
  if (state.status === 'loading') return <Booting />;
  if (state.status === 'error') return <Broken message={state.message} />;
  if (state.status === 'anon') return <Navigate to="/login" replace />;
  if (state.me.staff.role !== 'admin') {
    // A clean, plain refusal — not a blank screen and not a crash (§0.5).
    return (
      <Screen>
        <Title sub="Admin">Mulaqaat</Title>
        <Card>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            This screen is for admins only. Ask the owner if you need access.
          </p>
        </Card>
      </Screen>
    );
  }
  return <Admin />;
}

function Booting() {
  return (
    <Screen>
      <Title sub="Arjun Jewellers, Kotkapura">Mulaqaat</Title>
      <Card>
        <Loading label="Just a moment…" />
      </Card>
    </Screen>
  );
}

function Broken({ message }: { message: string }) {
  return (
    <Screen>
      <Title sub="Arjun Jewellers, Kotkapura">Mulaqaat</Title>
      <Card>
        <ErrorState message={message} onRetry={() => window.location.reload()} />
      </Card>
    </Screen>
  );
}

function NotFound() {
  return (
    <Screen>
      <Title sub="Arjun Jewellers, Kotkapura">Mulaqaat</Title>
      <Card>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>That page does not exist.</p>
      </Card>
    </Screen>
  );
}
